/**
 * Copy Button - Copy-to-clipboard component
 *
 * @author MNCA
 */
class CopyButton {
    static defaults = {
        text: null,              // Text to copy (null = use data-copy or URL)
        message: 'Link copied',  // Success message if data-copy-msg is empty or null
        duration: 3600,          // Duration of 'copied' state (ms)
        onCopy: null,            // Callback after successful copy
        onError: null            // Callback on error
    };

    constructor(element, options = {}) {
        this.element = element;
        this.options = { ...CopyButton.defaults, ...options };

		// Store instance on element
		element._copyButtonInstance = this;

        // Check browser capabilities
        if (!this._isSupported()) {
            this._handleUnsupported();
            return;
        }

        this._init();
    }

    _init() {
        this.element.addEventListener('click', (e) => this._handleClick(e));
    }

    _isSupported() {
        return navigator.clipboard && window.isSecureContext;
    }

    _handleUnsupported() {
        console.warn('CopyButton: Clipboard API unavailable');
        const li = this.element.closest('li');
        if (li) li.style.display = 'none';
    }

    async _handleClick(event) {
        event.preventDefault();

        const textToCopy = this._getTextToCopy();
        const message = this._getMessage();

        try {
            await navigator.clipboard.writeText(textToCopy);
            this._showSuccess(message);

            if (typeof this.options.onCopy === 'function') {
                this.options.onCopy(textToCopy);
            }
        } catch (error) {
            console.error('CopyButton: Failed to write to clipboard:', error);

            if (typeof this.options.onError === 'function') {
                this.options.onError(error);
            }
        }
    }

    _getTextToCopy() {
        return this.options.text
            || this.element.getAttribute('data-copy')
            || window.location.href;
    }

    _getMessage() {
        return this.element.getAttribute('data-copy-msg')
            || this.options.message;
    }

    _showSuccess(message) {
        const msgElement = this.element.lastElementChild;
        if (msgElement) {
            msgElement.textContent = message;
        }

        this.element.classList.add('copied');

        setTimeout(() => {
            this.element.classList.remove('copied');
        }, this.options.duration);
    }

    // Programmatically trigger copy
    async copy(text = null) {
        if (text) this.options.text = text;
        await this._handleClick(new Event('click'));
    }

    // Destroy instance
    destroy() {
        this.element.removeEventListener('click', this._handleClick);
		delete this.element._copyButtonInstance;
    }

    // Initialize all matching elements
    static initAll(selector = '.copy', options = {}) {
        const elements = document.querySelectorAll(selector);
        const instances = [];

        elements.forEach(element => {
            instances.push(new CopyButton(element, options));
        });

        return instances;
    }

    // Get instance from element
    static getInstance(element) {
        return element._copyButtonInstance || null;
    }
}

// Auto-initialization on DOMContentLoaded
if (typeof window !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        CopyButton.initAll('.copy');
    });
}

export default CopyButton;
